"use client";

import { useTranslations } from "next-intl";
import { DebugSection } from "./components/debug-primitives";
import type { DebugPanelTabContentProps } from "./debug-panel-tab-content.types";
import { ApiLogPanel } from "./panels/api-log-panel";
import { ConnectionsPanel } from "./panels/connections-panel";
import { ErrorLogPanel } from "./panels/error-log-panel";
import { FilesTab } from "./panels/files-tab";
import { FormInspectorPanel } from "./panels/form-inspector-panel";
import { I18nPanel } from "./panels/i18n-panel";
import { NavHistoryPanel } from "./panels/nav-history-panel";
import { PermissionPanel } from "./panels/permission-panel";
import { QueryCachePanel } from "./panels/query-cache-panel";
import { SessionContextPanel } from "./panels/session-context-panel";
import { VisualContextTab } from "./panels/visual-context-tab";

export function DebugPanelTabContent({
  activeTab,
  user,
  organizations,
  selectedOrganization,
  sessionContext,
  authIsLoading,
  authError,
  manifest,
  pathname,
  overlays,
}: DebugPanelTabContentProps) {
  const t = useTranslations("debug.sections");

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-3 sm:p-4 space-y-1.5">
        {activeTab === "visual" && (
          <VisualContextTab
            user={user}
            organizations={organizations}
            selectedOrganization={selectedOrganization}
            sessionContext={sessionContext}
            authIsLoading={authIsLoading}
            authError={authError}
          />
        )}
        {activeTab === "files" && (
          <FilesTab
            manifest={manifest}
            pathname={pathname}
            overlays={overlays}
          />
        )}
        {activeTab === "network" && (
          <DebugSection title={t("apiLog")} accent="border-blue-500">
            <ApiLogPanel />
          </DebugSection>
        )}
        {activeTab === "queries" && (
          <DebugSection title={t("queryCache")} accent="border-purple-500">
            <QueryCachePanel />
          </DebugSection>
        )}
        {activeTab === "session" && (
          <DebugSection title={t("sessionContext")} accent="border-emerald-500">
            <SessionContextPanel
              user={user}
              selectedOrganization={selectedOrganization}
              sessionContext={sessionContext}
            />
          </DebugSection>
        )}
        {activeTab === "i18n" && (
          <DebugSection title={t("missingI18n")} accent="border-red-500">
            <I18nPanel />
          </DebugSection>
        )}
        {activeTab === "sse" && (
          <DebugSection title={t("connections")} accent="border-cyan-500">
            <ConnectionsPanel />
          </DebugSection>
        )}
        {activeTab === "errors" && (
          <DebugSection title={t("errorLog")} accent="border-red-500">
            <ErrorLogPanel />
          </DebugSection>
        )}
        {activeTab === "permissions" && (
          <DebugSection title={t("permissions")} accent="border-violet-500">
            <PermissionPanel user={user} />
          </DebugSection>
        )}
        {activeTab === "forms" && (
          <DebugSection title={t("formInspector")} accent="border-amber-500">
            <FormInspectorPanel />
          </DebugSection>
        )}
        {activeTab === "nav" && (
          <DebugSection title={t("navHistory")} accent="border-indigo-500">
            <NavHistoryPanel />
          </DebugSection>
        )}
      </div>
    </div>
  );
}
